import React, { useState, useEffect } from 'react';
import {
	Row,
	Col,
	notification,
	Space,
	Pagination,
	Skeleton,
	Empty,
	Collapse,
} from 'antd';
import {
	XContainer,
	XButton,
	XThemeContainer,
	XThemeGradientButton,
	XThemeFilterCards,
	XThemeTable,
	XThemeInputText,
	XThemeFilterButton,
	XThemeLogsCards,
	XThemeTag,
	XThemeLogsTags,
} from '../../../styles/GlobalThemeStyle';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import HeaderFile from '../../../components/Header/HeaderFile';
import {
	getLogsByIdApi,
	getLogsByIdReset,
} from '../../../actions/getLogsByIdAction';
import { connect } from 'react-redux';
import moment from 'moment';

const ActivityLogDetails = (props) => {
	const { getLogsByIdState, getLogsById, getLogsByIdReset } = props;

	const params = useParams();
	const [searchValue, setSearchValue] = useState('');
	const [logs, setLogs] = useState([]);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});

	useEffect(() => {
		fetchLogs();
		return () => {
			getLogsByIdReset();
		};
	}, []);

	const fetchLogs = (
		page = 1,
		search = '',
		pageSize = pagination.pageSize,
	) => {
		getLogsById({
			id: params.id,
			page: page,
			limit: pageSize,
			search_value: search,
			// type: 'case', // You can make this dynamic if needed
		});
	};

	useEffect(() => {
		if (getLogsByIdState.apiState === 'success' && getLogsByIdState.data) {
			setLogs(getLogsByIdState.data.logs || []);
			setPagination((prev) => ({
				...prev,
				total: getLogsByIdState.data.pagination.total,
				current: getLogsByIdState.data.pagination.page,
			}));
		}
	}, [getLogsByIdState]);

	const handleSearch = () => {
		if (!searchValue.trim()) {
			return; // Don't make API call if search value is empty
		}
		fetchLogs(1, searchValue.trim());
	};

	const handleSearchClear = () => {
		setSearchValue('');
		setPagination((prev) => ({
			...prev,
			current: 1,
			pageSize: 10,
		}));
		fetchLogs(1, '', 10); // Reset search with default values
	};

	const handlePageChange = (page, pageSize) => {
		setPagination((prev) => ({
			...prev,
			current: page,
			pageSize: pageSize,
		}));
		fetchLogs(page, searchValue, pageSize);
	};

	const getLevelTagClass = (level) => {
		switch (level?.toUpperCase()) {
			case 'WARNING':
				return 'warning';
			case 'ERROR':
				return 'error';
			case 'INFO':
				return 'info';
			default:
				return 'default';
		}
	};

	// Helper function to format keys as readable labels
	const formatKeyAsLabel = (key) => {
		if (!key) return '';
		return key
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	};

	// Prioritize non-underscore versions of same data
	const getPreferredKey = (keys, preferUnderscored = false) => {
		if (!keys || keys.length === 0) return null;
		
		// For each key, check if a non-underscore version exists
		const keyMapping = {};
		keys.forEach(key => {
			const normalizedKey = key.toLowerCase().replace(/[_\s]/g, '');
			if (!keyMapping[normalizedKey] || (preferUnderscored ? key.includes('_') : !key.includes('_'))) {
				keyMapping[normalizedKey] = key;
			}
		});
		
		return Object.values(keyMapping);
	};

	// Arrays and objects that should take full width
	const fullWidthFields = [
		'past_history', 'present_complaint', 'disease_name', 'suggested_treatment',
		'Past History', 'Present Complaint', 'Disease Name', 'Suggested Treatment',
		'clinical_summary', 'input_query', 'response', 'missing_points_for_coverage',
		'Medicare Evaluation', 'NCCN References', 'retired_message', 'justification'
	];

	const isFullWidthField = (key, value) => {
		return fullWidthFields.includes(key) || 
			   (typeof value === 'string' && value.length > 100) ||
			   (Array.isArray(value) && value.some(item => typeof item === 'object'));
	};

	const renderNestedData = (data, level = 0) => {
		if (!data) return null;
		
		if (level === 0 && Object.keys(data).length === 0) {
			return <div className="text-muted">No data available</div>;
		}

		// Handle case where data might be a string, number, or simple value
		if (typeof data !== 'object') {
			return <div>{String(data)}</div>;
		}

		// For arrays
		if (Array.isArray(data)) {
			// If array of primitives
			if (data.every(item => typeof item !== 'object' || item === null)) {
				return (
					<ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
						{data.map((item, idx) => (
							<li key={idx} className="array-item">{String(item)}</li>
						))}
					</ul>
				);
			}
			
			// If array of objects
			return (
				<div className="array-of-objects">
					{data.map((item, idx) => (
						<div key={idx} className="array-object-item mb-2">
							{typeof item === 'object' && item !== null ? 
								renderNestedData(item, level + 1) : 
								String(item)}
						</div>
					))}
				</div>
			);
		}

		// Get keys, prioritizing non-underscore versions
		const allKeys = Object.keys(data);
		const preferredKeys = getPreferredKey(allKeys, false);
		
		// Group by field type (simple vs complex, full width vs normal)
		const simpleFields = {};
		const complexFields = {};
		
		preferredKeys.forEach(key => {
			const value = data[key];
			
			// Skip null or undefined values
			if (value === null || value === undefined) return;
			
			// Check if it should be full width or is complex
			const isComplex = typeof value === 'object' && value !== null;
			const isFullWidth = fullWidthFields.includes(key) || 
							   (typeof value === 'string' && value.length > 200);
			
			if (isComplex || isFullWidth) {
				complexFields[key] = value;
			} else {
				simpleFields[key] = value;
			}
		});

		return (
			<div className={level > 0 ? "nested-level" : ""}>
				{/* Render simple fields in a grid */}
				{Object.keys(simpleFields).length > 0 && (
					<Row gutter={[15, 15]}>
						{Object.entries(simpleFields).map(([key, value]) => (
							<Col xs={24} sm={12} key={key}>
								<div className="simple-field mb-2">
									<h6 className="heading">{formatKeyAsLabel(key)}</h6>
									<div className="headingTile">{String(value)}</div>
								</div>
							</Col>
						))}
					</Row>
				)}
				
				{/* Render complex fields with collapsible panels */}
				{Object.keys(complexFields).length > 0 && (
					<div className="complex-fields">
						{Object.entries(complexFields).map(([key, value]) => {
							// Special handling for response
							if (key === 'response') {
								return (
									<Collapse 
										key={key}
										className="custom-collapse mb-2"
										defaultActiveKey={level === 0 ? [key] : []}
									>
										<Collapse.Panel 
											header={<span className="nested-header">{formatKeyAsLabel(key)}</span>}
											key={key}
										>
											{renderNestedData(value, level + 1)}
										</Collapse.Panel>
									</Collapse>
								);
							}
							
							// Special handling for long text fields
							if (typeof value === 'string' && value.length > 200) {
								return (
									<div key={key} className="long-text-field mb-2">
										<h6 className="heading">{formatKeyAsLabel(key)}</h6>
										<div className="headingTile long-text">
											{value}
										</div>
									</div>
								);
							}
							
							// Arrays of simple values (non-objects)
							if (Array.isArray(value) && !value.some(item => typeof item === 'object' && item !== null)) {
								return (
									<div key={key} className="array-field mb-2">
										<Collapse 
											className="custom-collapse"
											defaultActiveKey={level === 0 ? [key] : []}
										>
											<Collapse.Panel 
												header={<span className="nested-header">{formatKeyAsLabel(key)}</span>}
												key={key}
											>
												<ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
													{value.map((item, idx) => (
														<li key={idx} className="array-item">{String(item)}</li>
													))}
												</ul>
											</Collapse.Panel>
										</Collapse>
									</div>
								);
							}
							
							// Other objects or arrays of objects
							return (
								<div key={key} className="object-field mb-2">
									<Collapse 
										className="custom-collapse"
										defaultActiveKey={level === 0 ? [key] : []}
									>
										<Collapse.Panel 
											header={<span className="nested-header">{formatKeyAsLabel(key)}</span>}
											key={key}
										>
											{renderNestedData(value, level + 1)}
										</Collapse.Panel>
									</Collapse>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	};

	const renderLogCard = (log) => (
		<XThemeLogsCards key={log._id} className="mb-3">
			<Row gutter={[15, 15]}>
				<Col xs={5} sm={3} lg={1}>
					<img
						className="img-fluid"
						src="/images/logs-icons.svg"
						alt=""
					/>
				</Col>
				<Col xs={19} sm={21} lg={23}>
					<Row gutter={[15, 15]}>
						{/* Basic Log Info */}
						<Col xs={24} sm={8} lg={6}>
							<h6 className="heading">Timestamp</h6>
							<h4 className="headingTile">
								{moment(log.timestamp).format(
									'DD-MM-YYYY HH:mm:ss',
								)}
							</h4>
						</Col>
						<Col xs={24} sm={8} lg={6}>
							<h6 className="heading">Level</h6>
							<XThemeLogsTags
								className={getLevelTagClass(log.level)}
							>
								{log.level || '-'}
							</XThemeLogsTags>
						</Col>
						<Col xs={24} sm={8} lg={12}>
							<h6 className="heading">Message</h6>
							<h4 className="headingTile">
								{log.message || '-'}
							</h4>
						</Col>

						{/* Data Section */}
						{log.data && (
							<Col xs={24}>
								<div className="data-section mt-3">
									<Collapse 
										className="data-main-collapse"
										defaultActiveKey={['data']}
									>
										<Collapse.Panel 
											header={<span className="nested-header">Data</span>}
											key="data"
										>
											{renderNestedData(log.data)}
										</Collapse.Panel>
									</Collapse>
								</div>
							</Col>
						)}
					</Row>
				</Col>
			</Row>
		</XThemeLogsCards>
	);

	const renderSkeletonLoader = () => (
		<Row gutter={[15, 15]}>
			<XThemeLogsCards className="mb-3" loading={true} />
		</Row>
	);

	// Add custom styling for the improved display
	const additionalStyles = `
		.data-section {
			border-top: 1px solid #f0f0f0;
			padding-top: 15px;
		}
		
		.nested-level {
			margin-left: 20px;
		}
		
		.data-main-collapse .ant-collapse-header {
			background-color: #e6f7ff !important;
			font-weight: 600;
		}
		
		.custom-collapse {
			background: white;
			border-radius: 4px;
			overflow: hidden;
			border: 1px solid #f0f0f0;
		}
		
		.custom-collapse .ant-collapse-header {
			background-color: #f7f7f7;
			padding: 10px 16px !important;
		}
		
		.nested-header {
			color: #1890ff;
			font-weight: 500;
			font-size: 14px;
		}
		
		.simple-field {
			background-color: #f9f9f9;
			padding: 12px;
			border-radius: 4px;
			height: 100%;
		}
		
		.array-item {
			padding: 5px 0;
			border-bottom: 1px dashed #f0f0f0;
		}
		
		.array-item:last-child {
			border-bottom: none;
		}
		
		.long-text {
			max-height: 400px;
			overflow-y: auto;
			background-color: #f5f5f5;
			padding: 10px;
			border-radius: 4px;
			white-space: pre-wrap;
		}
		
		.heading {
			color: #1890ff;
			margin-bottom: 6px;
			font-weight: 500;
			font-size: 14px;
		}
		
		.headingTile {
			color: rgba(0, 0, 0, 0.85);
			font-size: 14px;
			line-height: 1.5;
			word-break: break-word;
		}
		
		.array-object-item {
			padding: 8px;
			border: 1px solid #f0f0f0;
			border-radius: 4px;
			background-color: #fafafa;
		}
		
		.array-object-item:hover {
			background-color: #f5f5f5;
		}
	`;

	return (
		<>
			{props.displayHeader ? (
				<></>
			) : (
				<>
					<Col lg={24} xl={24} xs={0} md={0}>
						<Header headerTag="Log Details" />
					</Col>
					<Col lg={0} xl={0} xs={24} md={24}>
						<HeaderFile activeNav="Log Details" />
					</Col>
				</>
			)}
			<XThemeContainer fluid>
				<XThemeFilterCards className="smallTopBottomPadding mb-3">
					<Row gutter={[15, 15]} className="align-items-center">
						<Col
							xs={24}
							sm={16}
							md={12}
							lg={12}
							className="mb-3 mb-md-0"
						>
							<Space>
								<XThemeInputText
									size="large"
									allowClear
									placeholder="Search for logs"
									value={searchValue}
									onChange={(e) => {
										setSearchValue(e.target.value);
										if (!e.target.value) {
											// If input is cleared, reset search
											handleSearchClear();
										}
									}}
									onPressEnter={handleSearch}
								/>
								<XThemeFilterButton
									onClick={handleSearch}
									disabled={!searchValue.trim()}
								>
									Search
								</XThemeFilterButton>
							</Space>
						</Col>
					</Row>
				</XThemeFilterCards>

				{getLogsByIdState.apiState === 'loading' ? (
					<>{renderSkeletonLoader()}</>
				) : (
					<>
						{logs.length > 0 ? (
							<>
								{logs.map(renderLogCard)}
								<Row justify="end" className="mt-4">
									<Col>
										<Pagination
											current={pagination.current}
											total={pagination.total}
											pageSize={pagination.pageSize}
											onChange={handlePageChange}
											showSizeChanger={true}
											pageSizeOptions={[
												'10',
												'20',
												'50',
												'100',
											]}
										/>
									</Col>
								</Row>
							</>
						) : (
							<XThemeFilterCards className="text-center py-5">
								<Empty description="No logs found" />
							</XThemeFilterCards>
						)}
					</>
				)}
			</XThemeContainer>
			<style jsx global>{additionalStyles}</style>
		</>
	);
};

const mapStateToProps = (state) => ({
	getLogsByIdState: state.getLogsById,
});

const mapDispatchToProps = (dispatch) => ({
	getLogsById: (params) => dispatch(getLogsByIdApi(params)),
	getLogsByIdReset: () => dispatch(getLogsByIdReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ActivityLogDetails);
